import React, { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CONTENT_TYPES_META } from "@/lib/constants";
import {
  LayoutDashboard, FileText, Map, MapPin, Wallet, Wrench, BookOpen, File,
  Image, Mail, MessageSquare, Settings, LogOut, ExternalLink, Link2, TrendingUp,
} from "lucide-react";

const ICONS = { FileText, Map, MapPin, Wallet, Wrench, BookOpen, File };

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
    isActive ? "bg-vs-orange text-white" : "text-vs-text/70 hover:bg-vs-peach/40"
  }`;

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin — Vagabondaggio Selvaggio";
  }, []);

  return (
    <div className="min-h-screen bg-vs-bg flex">
      <aside className="w-64 shrink-0 bg-white border-r border-vs-text/10 hidden md:flex flex-col fixed h-screen overflow-y-auto">
        <div className="p-5 border-b border-vs-text/10">
          <div className="font-logo text-2xl text-vs-orange leading-none">Vagabondaggio</div>
          <div className="text-xs text-vs-text/50 font-bold mt-1">Pannello di controllo</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavLink to="/admin" end className={linkCls} data-testid="admin-nav-dashboard">
            <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} /> Dashboard
          </NavLink>
          <NavLink to="/admin/analytics" className={linkCls} data-testid="admin-nav-analytics">
            <TrendingUp className="w-5 h-5" strokeWidth={1.5} /> Analytics
          </NavLink>
          {Object.entries(CONTENT_TYPES_META).map(([key, meta]) => {
            const Icon = ICONS[meta.icon] || File;
            return (
              <NavLink key={key} to={`/admin/content/${key}`} className={linkCls} data-testid={`admin-nav-${key}`}>
                <Icon className="w-5 h-5" strokeWidth={1.5} /> {meta.label}
              </NavLink>
            );
          })}
          <div className="pt-2 mt-2 border-t border-vs-text/10 space-y-1">
            <NavLink to="/admin/media" className={linkCls} data-testid="admin-nav-media"><Image className="w-5 h-5" strokeWidth={1.5} /> Media</NavLink>
            <NavLink to="/admin/bio" className={linkCls} data-testid="admin-nav-bio"><Link2 className="w-5 h-5" strokeWidth={1.5} /> Pagina Bio / Link</NavLink>
            <NavLink to="/admin/newsletter" className={linkCls} data-testid="admin-nav-newsletter"><Mail className="w-5 h-5" strokeWidth={1.5} /> Newsletter</NavLink>
            <NavLink to="/admin/contacts" className={linkCls} data-testid="admin-nav-contacts"><MessageSquare className="w-5 h-5" strokeWidth={1.5} /> Messaggi</NavLink>
            <NavLink to="/admin/settings" className={linkCls} data-testid="admin-nav-settings"><Settings className="w-5 h-5" strokeWidth={1.5} /> Impostazioni sito</NavLink>
          </div>
        </nav>
        <div className="p-3 border-t border-vs-text/10">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold text-vs-text/70 hover:bg-vs-peach/40">
            <ExternalLink className="w-5 h-5" strokeWidth={1.5} /> Vedi il sito
          </a>
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold text-red-600 hover:bg-red-50" data-testid="admin-logout">
            <LogOut className="w-5 h-5" strokeWidth={1.5} /> Esci
          </button>
        </div>
      </aside>

      <div className="flex-1 md:ml-64">
        <header className="md:hidden bg-white border-b border-vs-text/10 px-5 h-14 flex items-center justify-between">
          <span className="font-logo text-xl text-vs-orange">Vagabondaggio</span>
          <button onClick={logout} className="text-red-600"><LogOut className="w-5 h-5" /></button>
        </header>
        <div className="md:hidden bg-white border-b border-vs-text/10 px-3 py-2 flex gap-1 overflow-x-auto no-scrollbar">
          <button onClick={() => navigate("/admin")} className="px-3 py-1.5 text-xs font-bold rounded-lg bg-vs-peach/40 whitespace-nowrap">Dashboard</button>
          {Object.entries(CONTENT_TYPES_META).map(([key, meta]) => (
            <button key={key} onClick={() => navigate(`/admin/content/${key}`)} className="px-3 py-1.5 text-xs font-bold rounded-lg bg-vs-peach/40 whitespace-nowrap">{meta.singular}</button>
          ))}
          <button onClick={() => navigate("/admin/media")} className="px-3 py-1.5 text-xs font-bold rounded-lg bg-vs-peach/40 whitespace-nowrap">Media</button>
          <button onClick={() => navigate("/admin/settings")} className="px-3 py-1.5 text-xs font-bold rounded-lg bg-vs-peach/40 whitespace-nowrap">Impostazioni</button>
        </div>
        <div className="p-5 md:p-8 max-w-6xl mx-auto">{children}</div>
      </div>
    </div>
  );
}
